// =============================================================================
// MeuDriver — useSubscription
// Hook para gerenciar a assinatura Premium do aluno.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { StudentSubscriptionRow } from '@/types/database'
import { toast } from 'sonner'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

/** Resposta de cancel-*-subscription (ver _shared/assinatura.ts). */
interface CancelResult {
  ok: boolean
  mode: 'refunded' | 'end_of_period' | 'immediate'
  access_until: string | null
}

function cancelMessage(plano: string, r: CancelResult): string {
  if (r.mode === 'refunded') return `Assinatura ${plano} cancelada e valor estornado no seu cartão.`
  if (r.mode === 'end_of_period' && r.access_until) {
    const ate = format(parseISO(r.access_until), "dd 'de' MMMM", { locale: ptBR })
    return `Assinatura ${plano} cancelada. Seus benefícios continuam até ${ate} e não haverá nova cobrança.`
  }
  return `Assinatura ${plano} cancelada.`
}

const subscriptionKeys = {
  student: (userId: string) => ['subscription', 'student', userId] as const,
}

async function fetchStudentSubscription(
  userId: string,
): Promise<StudentSubscriptionRow | null> {
  const { data, error } = await supabase
    .from('student_subscriptions')
    .select('*')
    .eq('student_id', userId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data as StudentSubscriptionRow | null
}

/** Retorna a assinatura Premium ativa do aluno autenticado, ou null. */
export function useSubscription() {
  const { user } = useAuth()

  return useQuery<StudentSubscriptionRow | null, Error>({
    queryKey: subscriptionKeys.student(user?.id ?? ''),
    queryFn: () => fetchStudentSubscription(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
  })
}

/** Inicia o fluxo de assinatura Premium (redireciona para o checkout de cartão do Asaas). */
export function useCreateSubscription() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<{ checkout_url: string }, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ checkout_url: string }>(
        'create-subscription',
        { body: { plan: 'premium' } },
      )
      if (error) throw new Error(error.message)
      if (!data?.checkout_url) throw new Error('checkout_url ausente na resposta')
      return data
    },
    onSuccess: (data) => {
      window.location.href = data.checkout_url
    },
    onError: (err) => {
      console.error('[useCreateSubscription]', err)
      toast.error('Erro ao iniciar assinatura. Tente novamente.')
    },
    onSettled: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.student(user.id) })
      }
    },
  })
}

/** Cancela a assinatura Premium do aluno. */
export function useCancelSubscription() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<CancelResult, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<CancelResult>('cancel-subscription', { body: {} })
      if (error) throw new Error(error.message)
      return data ?? { ok: true, mode: 'immediate', access_until: null }
    },
    onSuccess: (data) => {
      toast.success(cancelMessage('Premium', data))
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.student(user.id) })
      }
    },
    onError: (err) => {
      console.error('[useCancelSubscription]', err)
      toast.error('Erro ao cancelar assinatura. Tente novamente.')
    },
  })
}
